import React, { useContext, useMemo } from 'react';
import Select from 'react-select';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';

import ColorPicker from './ColorPicker';
import LabelColorSlider from './LabelColorSlider';
import { LanguageContext } from '../context/LanguageContext';
import commonTranslations from '../languages/common.json';
import {
  getThemeColorKey,
  getThemeColorValue,
  normalizeThemeMode,
} from '../utils/themeColors';

const toggleStyle = { color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' };

const TextTitleSettings = ({
  edit,
  setEdit,
  alignments = [],
  devices = [],
  validDatastreams = [],
  deviceOptions = [],
}) => {
  const { lang } = useContext(LanguageContext);

  const t = (commonTranslations?.[lang]?.common?.textTitleSettings || {}) ?? {};

  const alignmentOptions = useMemo(() => {
    if (Array.isArray(alignments) && alignments.length > 0) return alignments;
    return [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' },
    ];
  }, [alignments]);

  const fontFamilies = useMemo(
    () => [
      { label: 'Inherit', value: '' },
      { label: 'Arial', value: 'Arial, sans-serif' },
      { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
      { label: 'Roboto', value: 'Roboto, system-ui, sans-serif' },
      { label: 'Poppins', value: 'Poppins, system-ui, sans-serif' },
      { label: 'Montserrat', value: 'Montserrat, system-ui, sans-serif' },
      { label: 'Courier New', value: '"Courier New", monospace' },
    ],
    []
  );

  const fontWeights = useMemo(
    () => [
      { label: 'Inherit', value: '' },
      { label: 'Normal (400)', value: '400' },
      { label: 'Medium (500)', value: '500' },
      { label: 'SemiBold (600)', value: '600' },
      { label: 'Bold (700)', value: '700' },
      { label: 'ExtraBold (800)', value: '800' },
    ],
    []
  );

  const fontStyles = useMemo(
    () => [
      { label: 'Normal', value: 'normal' },
      { label: 'Italic', value: 'italic' },
    ],
    []
  );

  const decorations = useMemo(
    () => [
      { label: 'None', value: 'none' },
      { label: 'Underline', value: 'underline' },
      { label: 'Line-through', value: 'line-through' },
      { label: 'Overline', value: 'overline' },
    ],
    []
  );

  const updateProps = (patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...prev.props,
        ...patch,
      },
    }));

  const selectedDevice = useMemo(
    () => (devices || []).find((device) => Number(device?.id) === Number(edit?.deviceId)) || null,
    [devices, edit?.deviceId]
  );
  const datastreamSource = useMemo(() => {
    const deviceStreams = selectedDevice?.template?.datastreams;
    if (Array.isArray(deviceStreams) && deviceStreams.length > 0) {
      return deviceStreams.filter(Boolean);
    }
    return (validDatastreams || []).filter(Boolean);
  }, [selectedDevice, validDatastreams]);
  const datastreamOptions = useMemo(
    () => datastreamSource.map((ds) => ({
      label: `${ds.name || ds.pin || ds.id} [${ds.pin || ds.id}]`,
      value: ds.id,
    })),
    [datastreamSource]
  );
  const selectedDatastream = useMemo(() => {
    if (!edit?.dataId && !edit?.dataPin) return null;
    return datastreamSource.find((ds) =>
      Number(ds?.id) === Number(edit?.dataId) ||
      String(ds?.pin || '') === String(edit?.dataPin || '')
    ) || null;
  }, [datastreamSource, edit?.dataId, edit?.dataPin]);

  const themeMode = normalizeThemeMode(edit?.props?.themeMode);
  const isLight = themeMode === 'light';

  const titleValue = edit?.props?.title ?? '';
  const titleSize = Number(edit?.props?.titleSize ?? 20);
  const color = getThemeColorValue(edit?.props, 'color', themeMode, '#00f0f0');
  const alignment = edit?.props?.alignment ?? 'left';
  const fontFamily = edit?.props?.fontFamily ?? '';
  const fontWeight = edit?.props?.fontWeight ?? '';
  const fontStyle = edit?.props?.fontStyle ?? 'normal';
  const textDecoration = edit?.props?.textDecoration ?? 'none';
  const minValue = selectedDatastream?.min ?? edit?.props?.min ?? 0;
  const maxValue = selectedDatastream?.max ?? edit?.props?.max ?? 100;

  return (
    <div className="edit-content-part" style={{ flex: 0.65 }}>
      {Array.isArray(devices) && devices.length > 0 ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
            {t.deviceLabel || 'Device'}
          </label>
          <Select
            classNamePrefix="react-select"
            options={deviceOptions}
            value={deviceOptions.find((device) => Number(device.value) === Number(edit?.deviceId)) || null}
            onChange={(selectedOption) => {
              const nextDeviceId = selectedOption ? Number(selectedOption.value) : 0;
              setEdit((prev) => ({
                ...prev,
                deviceId: nextDeviceId,
                dataId: null,
                dataPin: null,
              }));
            }}
            isClearable={true}
          />
        </div>
      ) : null}

      {datastreamOptions.length > 0 ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
            {t.datastreamLabel || 'Datastream'}
          </label>
          <Select
            classNamePrefix="react-select"
            options={datastreamOptions}
            value={datastreamOptions.find((option) => Number(option.value) === Number(selectedDatastream?.id)) || null}
            onChange={(selectedOption) => {
              const ds = datastreamSource.find((item) => Number(item?.id) === Number(selectedOption?.value)) || null;
              setEdit((prev) => ({
                ...prev,
                dataId: ds?.id ?? null,
                dataPin: ds?.pin ?? null,
                props: {
                  ...(prev.props || {}),
                  value: ds?.defaultValue ?? ds?.value ?? prev.props?.value ?? 0,
                  min: ds?.min ?? prev.props?.min ?? 0,
                  max: ds?.max ?? prev.props?.max ?? 100,
                },
              }));
            }}
            isClearable={true}
          />
        </div>
      ) : null}

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.titleLabel || 'Title'}
        </label>
        <textarea
          className="add-column"
          rows={3}
          placeholder={t.titlePlaceholder || 'Enter title text'}
          style={{
            padding: '8px 10px',
            resize: 'vertical',
            color: 'var(--cell--color)',
            caretColor: 'var(--cell--color)',
            backgroundColor: 'var(--part-background)',
            border: '1px solid var(--header-background)',
            borderRadius: 8,
          }}
          value={titleValue}
          onChange={(e) => updateProps({ title: e.target.value })}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontSizeLabel || 'Font size'}
        </label>
        <input
          type="number"
          className="add-column"
          min={8}
          max={200}
          value={Number.isFinite(titleSize) ? titleSize : 20}
          onChange={(e) => updateProps({ titleSize: Number(e.target.value || 0) })}
          style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.changeColorBasedOnValue || 'Change color based on value'}
        </label>
        {edit?.props?.changeColorBasedOnValue ? (
          <ToggleOn
            style={toggleStyle}
            onClick={() => updateProps({ changeColorBasedOnValue: false })}
          />
        ) : (
          <ToggleOff
            style={toggleStyle}
            onClick={() => updateProps({ changeColorBasedOnValue: true })}
          />
        )}
      </div>

      {edit?.props?.changeColorBasedOnValue ? (
        <>
          <div className="form-item">
            <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
              {t.colorGrading || 'Smooth Gradient Transition'}
            </label>
            {edit?.props?.colorGrading ? (
              <ToggleOn
                style={toggleStyle}
                onClick={() => updateProps({ colorGrading: false })}
              />
            ) : (
              <ToggleOff
                style={toggleStyle}
                onClick={() => updateProps({ colorGrading: true })}
              />
            )}
          </div>

          <LabelColorSlider
            minValue={minValue}
            maxValue={maxValue}
            stops={getThemeColorValue(edit?.props, 'colors', themeMode, [])}
            colorGrading={Boolean(edit?.props?.colorGrading)}
            setStops={(updated) => updateProps({ [getThemeColorKey('colors', themeMode)]: updated })}
          />
        </>
      ) : (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
            {(t.colorLabel || 'Text color') + ` (${isLight ? 'Light' : 'Dark'})`}
          </label>
          <ColorPicker
            id="text-title-color"
            color={color}
            setColor={(c) => updateProps({ [getThemeColorKey('color', themeMode)]: c })}
          />
        </div>
      )}

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.alignmentLabel || 'Alignment'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={alignmentOptions}
          value={alignmentOptions.find((o) => o.value === alignment) || null}
          onChange={(selectedOption) => updateProps({ alignment: selectedOption ? selectedOption.value : 'left' })}
          isClearable={true}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontFamilyLabel || 'Font family'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={fontFamilies}
          value={fontFamilies.find((o) => o.value === fontFamily) || fontFamilies[0]}
          onChange={(selectedOption) => updateProps({ fontFamily: selectedOption ? selectedOption.value : '' })}
          isClearable={true}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontWeightLabel || 'Font weight'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={fontWeights}
          value={fontWeights.find((o) => o.value === String(fontWeight)) || fontWeights[0]}
          onChange={(selectedOption) => updateProps({ fontWeight: selectedOption ? selectedOption.value : '' })}
          isClearable={true}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontStyleLabel || 'Font style'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={fontStyles}
          value={fontStyles.find((o) => o.value === fontStyle) || fontStyles[0]}
          onChange={(selectedOption) => updateProps({ fontStyle: selectedOption ? selectedOption.value : 'normal' })}
          isClearable={false}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.textDecorationLabel || 'Text decoration'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={decorations}
          value={decorations.find((o) => o.value === textDecoration) || decorations[0]}
          onChange={(selectedOption) => updateProps({ textDecoration: selectedOption ? selectedOption.value : 'none' })}
          isClearable={false}
        />
      </div>
    </div>
  );
};

export default TextTitleSettings;
